import { Injectable } from '@angular/core';
import { Query } from '@angular/fire/compat/firestore';
import { DocumentData, WhereFilterOp } from '@angular/fire/firestore';
import { OutstandingRequisitionsResponse } from '@fnModels/requisitions';
import { OutstandingUndertakingsResponse } from '@fnModels/undertakings';
import { BusinessesServiceFunc } from '@functions/businesses.service';
import { RequisitionsFunctions } from '@functions/requisitions.serivce';
import { UndertakingsFunctions } from '@functions/undertakings.service';
import {
	BusinessUser,
	DATE_FORMAT,
	DefaultDashboardSelection,
	DefaultDashboardSelectionType,
} from '@models/businesses';
import { Matter, MatterFileType, MatterStatus } from '@models/matter';
import {
	Action,
	NgxsOnInit,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { MatterRepo } from '@repos/matter/matter.repo';
import { AuthActions, AuthState } from '@state/auth';
import { SetDefaultDashboardSelection } from '@state/business-users/business-users.actions';
import { BusinessAddressesState } from '@state/business/address';
import { endOfDay, format, startOfDay } from 'date-fns';
import { orderBy } from 'lodash';
import LogRocket from 'logrocket';
import { Subscription } from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';
import * as actions from './dashboard.actions';
import { DashboardModel, getDefaultDashboardState } from './dashboard.model';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { UserSelectors } from '@state/global/user';
import { Logger } from '@core/logger';
import { LlcState } from '@state/llc/llc.state';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { CONNECTION_STATUS } from '@state/llc/llc.model';
import { BusinessState } from '@state/business';
import { UserProfileState } from '@state/user-profile';

interface FirebaseDashboardFilter {
	property: string;
	value: string;
}

export enum FileSelectOptions {
	ALL = 'all',
	PURCHASES = 'purchase',
	SALES = 'sale',
}

export interface MortgageInboxModel {
	llcStatus: CONNECTION_STATUS;
	currentUser: BusinessUser;
	syncEnabledLawyers: BusinessUser[];
	lawyers: BusinessUser[];
	treefortEnabled?: boolean;
	isSyncIntegrationConnected?: boolean;
}

export interface UpcomingRequisitionCounts {
	[FileSelectOptions.ALL]: number;
	[FileSelectOptions.PURCHASES]: number;
	[FileSelectOptions.SALES]: number;
}

export type FilterQueryObject = [string, WhereFilterOp, unknown] | null;

const filterTypeToPropertyMap = new Map([
	[DefaultDashboardSelectionType.CLERK, 'clerkAssigned.id'],
	[DefaultDashboardSelectionType.LAWYER, 'lawyerAssigned.id'],
	[DefaultDashboardSelectionType.OFFICE, 'officeId'],
]);

export interface GetWhereClauseResponse {
	query: Query<DocumentData>;
	postOfficeIdFilter?: string;
}

type Ctx = StateContext<DashboardModel>;

@State<DashboardModel>({
	name: 'dashboardData',
	defaults: { ...getDefaultDashboardState() },
})
@Injectable()
export class DashboardState implements NgxsOnInit {
	private appointmentsQuerySubscription?: Subscription;
	private closingsQuerySubscription?: Subscription;
	private occupanciesQuerySubscription?: Subscription;
	private requisitionsQuerySubscription?: Subscription;
	private upcomingRequisitionsSubscription!: Subscription;

	@Selector([
		LlcState.status,
		BusinessUsersState.getLoggedInBusinessUser,
		BusinessUsersState.getSyncEnabledLawyers,
		BusinessUsersState.getLawyers,
	])
	static getMortgageInboxData(
		llcStatus: CONNECTION_STATUS,
		currentUser: BusinessUser,
		syncEnabledLawyers: BusinessUser[],
		lawyers: BusinessUser[],
	): MortgageInboxModel {
		return {
			llcStatus,
			currentUser,
			syncEnabledLawyers,
			lawyers,
		};
	}

	@Selector([
		DashboardState.getMortgageInboxData,
		BusinessUsersState.isSyncIntegrationConnected,
		BusinessState.getTreefortConnected,
	])
	static getDashboardInboxData(
		mortgageInboxData: MortgageInboxModel,
		isSyncIntegrationConnected: boolean,
		treefortEnabled: boolean,
	): MortgageInboxModel {
		const { llcStatus, currentUser, syncEnabledLawyers, lawyers } =
			mortgageInboxData;
		return {
			llcStatus,
			currentUser,
			syncEnabledLawyers,
			lawyers,
			treefortEnabled,
			isSyncIntegrationConnected,
		};
	}

	@Selector([DashboardState])
	static getFilter(state: DashboardModel): DefaultDashboardSelection {
		return state.filter;
	}

	@Selector([DashboardState.getFilter, BusinessAddressesState.primaryId])
	static filterAsQueryObject(
		filter: DefaultDashboardSelection,
		primaryId: string,
	): FilterQueryObject {
		const mappedProperty = filterTypeToPropertyMap.get(filter?.type);
		if (mappedProperty) {
			if (
				filter.type === DefaultDashboardSelectionType.OFFICE &&
				filter.id === primaryId
			) {
				return null;
			}
			return [mappedProperty, '==', filter?.id];
		}
		return null;
	}

	@Selector([DashboardState.getFilter, BusinessAddressesState.primaryId])
	static optionalOfficeIdFilter(
		filter: DefaultDashboardSelection,
		primaryId: string,
	): string {
		if (
			filter?.type === DefaultDashboardSelectionType.OFFICE &&
			filter?.id === primaryId
		) {
			return filter.id;
		}
		return null;
	}

	@Selector([DashboardState])
	static initialized(state: DashboardModel): boolean {
		return state.initialized;
	}

	@Selector([DashboardState])
	static getDate(state: DashboardModel): number | string {
		return state.date;
	}

	@Selector([DashboardState])
	static outstandingUndertakings(
		state: DashboardModel,
	): OutstandingUndertakingsResponse[] {
		return state.outstandingUndertakings;
	}

	@Selector([DashboardState.outstandingUndertakings])
	static outstandingUndertakingsCount(
		undertakings: OutstandingUndertakingsResponse[],
	): number {
		return undertakings?.length ?? 0;
	}

	@Selector([DashboardState])
	static loadingOutstandingUndertakings(state: DashboardModel): boolean {
		return state.loadingOutstandingUndertakings;
	}

	@Selector([DashboardState])
	static outstandingRequisitions(
		state: DashboardModel,
	): OutstandingRequisitionsResponse[] {
		return state.outstandingRequisitions;
	}

	@Selector([DashboardState.outstandingRequisitions])
	static outstandingRequisitionsCount(
		requisitions: OutstandingRequisitionsResponse[],
	): number {
		return requisitions?.length ?? 0;
	}

	@Selector([DashboardState])
	static loadingOutstandingRequisitions(state: DashboardModel): boolean {
		return state.loadingOutstandingRequisitions;
	}

	@Selector([DashboardState])
	static lastUpdated(state: DashboardModel): number {
		return state.lastUpdated;
	}

	@Selector([DashboardState.getDate])
	static selectedDate(date: number | string): Date {
		return new Date(date);
	}

	@Selector([DashboardState])
	static closing(state: DashboardModel): Matter[] {
		return state.closings;
	}

	@Selector([DashboardState.closing])
	static closingCount(matters: Matter[]): number {
		return matters?.length ?? 0;
	}

	@Selector([DashboardState])
	static occupancies(state: DashboardModel): Matter[] {
		return state.occupancies;
	}

	@Selector([DashboardState])
	static appointments(state: DashboardModel): Matter[] {
		return state.appointments;
	}

	@Selector([DashboardState.appointments])
	static appointmentsOrdered(appointments: Matter[]): Matter[] {
		return orderBy(appointments || [], 'appointmentTime', 'asc');
	}

	@Selector([DashboardState.appointments])
	static appointmentsCount(matters: Matter[]): number {
		return matters?.length ?? 0;
	}

	@Selector([DashboardState])
	static requisitions(state: DashboardModel): Matter[] {
		return state.requisitions;
	}

	@Selector([DashboardState.requisitions])
	static requisitionsCount(matters: Matter[]): number {
		return matters?.length ?? 0;
	}

	@Selector([DashboardState])
	static upcomingRequisitionsSale(state: DashboardModel): Matter[] {
		return state.upcomingRequisitionsSale;
	}

	@Selector([DashboardState])
	static loadingUpcomingRequisitionsSale(state: DashboardModel): boolean {
		return !!state.loadingUpcomingRequisitionsSale;
	}

	@Selector([DashboardState])
	static upcomingRequisitionsPurchase(state: DashboardModel): Matter[] {
		return state.upcomingRequisitionsPurchase;
	}

	@Selector([DashboardState])
	static loadingUpcomingRequisitionsPurchase(state: DashboardModel): boolean {
		return !!state.loadingUpcomingRequisitionsPurchase;
	}

	@Selector([
		DashboardState.loadingUpcomingRequisitionsPurchase,
		DashboardState.loadingUpcomingRequisitionsSale,
	])
	static loadingUpcomingRequisitions(
		purchases: boolean,
		sales: boolean,
	): boolean {
		return purchases && sales;
	}

	@Selector([
		DashboardState.loadingOutstandingRequisitions,
		DashboardState.loadingOutstandingUndertakings,
	])
	static loadingOutstandingItems(
		requisitions: boolean,
		undertakings: boolean,
	): boolean {
		return requisitions && undertakings;
	}

	@Selector([
		DashboardState.upcomingRequisitionsSale,
		DashboardState.upcomingRequisitionsPurchase,
	])
	static upcomingRequisitionCounts(
		sales: Matter[],
		purchases: Matter[],
	): UpcomingRequisitionCounts {
		const countSales = sales?.length ?? 0;
		const countPurchases = purchases?.length ?? 0;

		return {
			[FileSelectOptions.ALL]: countSales + countPurchases,
			[FileSelectOptions.PURCHASES]: countPurchases,
			[FileSelectOptions.SALES]: countSales,
		};
	}

	@SelectSnapshot(BusinessAddressesState.primaryId) primaryBusinessId: string;

	constructor(
		private matterRepo: MatterRepo,
		private undertakingsFunctions: UndertakingsFunctions,
		private requisitionsFunctions: RequisitionsFunctions,
		private businessesServiceFunc: BusinessesServiceFunc,
		private store: Store,
	) {}

	ngxsOnInit(ctx: Ctx) {
		this.store
			.select(BusinessState.getLoaded)
			.pipe(
				filter((x) => !!x),
				switchMap(() =>
					this.store.select(UserSelectors.getDashboardDefault).pipe(
						filter((x) => !!x),
						take(1),
					),
				),
			)
			.subscribe((_dashboardDefault) => {
				this.unsubscribeAll();
				ctx.setState({ ...getDefaultDashboardState() });
				ctx.dispatch(
					new actions.Search({
						date: new Date().getTime(),
						filter: _dashboardDefault,
					}),
				);
			});
	}

	@Action(AuthActions.Logout)
	@Action([AuthActions.UserAuthUpdated])
	onAuthUserUpdated(ctx: Ctx, { user }: AuthActions.UserAuthUpdated): void {
		if (!user) {
			this.unsubscribeAll();
			ctx.setState({ ...getDefaultDashboardState() });
		}
	}

	@Action(actions.Filter)
	filter(ctx: Ctx, { filter }: actions.Filter): void {
		this.store.dispatch(new SetDefaultDashboardSelection(filter));
		this.businessesServiceFunc.updateUserDashboardDefault(filter).toPromise();
		this._search(ctx, { filter });
	}

	@Action(actions.SetDate)
	setDate(ctx: Ctx, { date }: actions.SetDate): void {
		this._search(ctx, { date });
	}

	@Action(actions.Search)
	search(ctx: Ctx, { payload }: actions.Search): void {
		this._search(ctx, { filter: payload.filter, date: payload.date });
	}

	private getWhereClause(
		queryFn: Query<DocumentData>,
		filter: FirebaseDashboardFilter,
		filterType: DefaultDashboardSelectionType,
	): GetWhereClauseResponse {
		const postOfficeIdFilter =
			filterType === DefaultDashboardSelectionType.OFFICE &&
			filter.value === this.primaryBusinessId
				? filter.value
				: null;
		if (
			filterType === DefaultDashboardSelectionType.ALL ||
			postOfficeIdFilter
		) {
			return {
				query: queryFn,
				postOfficeIdFilter,
			};
		}

		return {
			query: queryFn.where(filter.property, '==', filter.value),
		};
	}

	private _search(
		ctx: Ctx,
		update: Partial<Pick<DashboardModel, 'date' | 'filter'>>,
	) {
		// Unsubscribing from previous subscription, this needs to be before resetting the state
		this.unsubscribeAll();

		const isUserLoggedIn = this.store.selectSnapshot(AuthState.isUserLoggedIn);
		if (!isUserLoggedIn) return;

		ctx.patchState({
			...update,
			initialized: true,
		});

		const _state = ctx.getState();
		const date = new Date(_state.date);
		const dateFormatted = format(date, DATE_FORMAT);
		const { filter } = _state;

		const start = startOfDay(date).getTime();
		const end = endOfDay(date).getTime();

		const dashboardFilter: FirebaseDashboardFilter = {
			property: '',
			value: '',
		};

		dashboardFilter.value = filter.id;

		switch (filter.type) {
			case DefaultDashboardSelectionType.CLERK:
				dashboardFilter.property = 'clerkAssigned.id';
				break;
			case DefaultDashboardSelectionType.LAWYER:
				dashboardFilter.property = 'lawyerAssigned.id';
				break;
			case DefaultDashboardSelectionType.OFFICE:
				dashboardFilter.property = 'officeId';
				// shit, these aren't showing up
				break;
		}

		// begin gross hacks
		let occupanciesQueryPostOffice!: string;
		let upcomingRequisitionsPostOffice!: string;

		this.occupanciesQuerySubscription = this.matterRepo
			.All(null, (x) => {
				const baseQuery = x.where('occupancyDate', '==', dateFormatted);
				const resp = this.getWhereClause(
					baseQuery,
					dashboardFilter,
					filter.type,
				);
				occupanciesQueryPostOffice = resp.postOfficeIdFilter;
				return resp.query;
			})
			.pipe(
				map((matters: Matter[]) =>
					occupanciesQueryPostOffice
						? matters.filter((all) =>
								this.filterByOfficeIdOrNull(all, occupanciesQueryPostOffice),
							)
						: matters,
				),
			)
			.subscribe((matters: Matter[]) => {
				ctx.patchState({ occupancies: matters });
			});

		this.closingsQuerySubscription = this.matterRepo
			.All(null, (x) => {
				const baseQuery = x.where('closingDate', '==', dateFormatted);
				const resp = this.getWhereClause(
					baseQuery,
					dashboardFilter,
					filter.type,
				);
				return resp.query;
			})
			.pipe(
				map((matters: Matter[]) =>
					occupanciesQueryPostOffice
						? matters.filter((all) =>
								this.filterByOfficeIdOrNull(all, occupanciesQueryPostOffice),
							)
						: matters,
				),
			)
			.subscribe((matters: Matter[]) => {
				ctx.patchState({ closings: matters });
			});

		this.appointmentsQuerySubscription = this.matterRepo
			.All(null, (x) => {
				const baseQuery = x
					.where('appointment.appointmentTime', '>=', start)
					.where('appointment.appointmentTime', '<=', end);
				const resp = this.getWhereClause(
					baseQuery,
					dashboardFilter,
					filter.type,
				);
				return resp.query;
			})
			.pipe(
				map((matters: Matter[]) =>
					occupanciesQueryPostOffice
						? matters.filter((all) =>
								this.filterByOfficeIdOrNull(all, occupanciesQueryPostOffice),
							)
						: matters,
				),
			)
			.subscribe((matters: Matter[]) => {
				ctx.patchState({ appointments: matters });
			});

		this.requisitionsQuerySubscription = this.matterRepo
			.All(null, (x) => {
				const baseQuery = x.where(
					'titleSearchCompletionDate',
					'==',
					dateFormatted,
				);
				const resp = this.getWhereClause(
					baseQuery,
					dashboardFilter,
					filter.type,
				);
				return resp.query;
			})
			.pipe(
				map((matters: Matter[]) =>
					occupanciesQueryPostOffice
						? matters.filter((all) =>
								this.filterByOfficeIdOrNull(all, occupanciesQueryPostOffice),
							)
						: matters,
				),
			)
			.subscribe((matters: Matter[]) => {
				ctx.patchState({ requisitions: matters });
			});

		if (update.filter) {
			const midnight = new Date(new Date().setHours(0, 0, 0, 0));
			const nextMonthish = new Date(midnight).setDate(midnight.getDate() + 31);
			const minUpcomingDate = format(midnight, DATE_FORMAT);
			const maxUpcomingDate = format(nextMonthish, DATE_FORMAT);

			LogRocket.log('Filter Changed dashboar.state:_search', {
				filterChange: true,
				minUpcomingDate,
				maxUpcomingDate,
			});

			ctx.patchState({
				loadingUpcomingRequisitionsSale: true,
				loadingOutstandingUndertakings: true,
				loadingUpcomingRequisitionsPurchase: true,
				loadingOutstandingRequisitions: true,
			});

			this.upcomingRequisitionsSubscription = this.matterRepo
				.All(null, (x) => {
					const baseQuery = x
						.where('status', '==', MatterStatus.ACTIVE)
						.orderBy('titleSearchCompletionDate')
						.where('titleSearchCompletionDate', '>=', minUpcomingDate)
						.where('titleSearchCompletionDate', '<=', maxUpcomingDate);
					const resp = this.getWhereClause(
						baseQuery,
						dashboardFilter,
						filter.type,
					);
					upcomingRequisitionsPostOffice = resp.postOfficeIdFilter;
					return resp.query;
				})
				.pipe(
					map((matters: Matter[]) => {
						const filteredSortedMatters = matters.reduce(
							(acc, matter) => {
								// skip matters that don't have a titleSearchCompletionDate or where requisitions have already been sent and then make sure that matter is related to the correct office Id
								if (
									!!matter?.requisitionStatus?.uploaded ||
									!matter?.titleSearchCompletionDate ||
									(upcomingRequisitionsPostOffice &&
										!this.filterByOfficeIdOrNull(
											matter,
											upcomingRequisitionsPostOffice,
										))
								) {
									return acc;
								}

								// sort the matters by file type to conserve bandwidth for now
								if (matter.fileType === MatterFileType.SALE) {
									acc.upcomingRequisitionsSale.push(matter);
								} else if (matter.fileType === MatterFileType.PURCHASE) {
									acc.upcomingRequisitionsPurchase.push(matter);
								}

								return acc;
							},
							{
								upcomingRequisitionsSale: [] as Matter[],
								upcomingRequisitionsPurchase: [] as Matter[],
							},
						);

						return filteredSortedMatters;
					}),
				)
				.subscribe(
					({ upcomingRequisitionsSale, upcomingRequisitionsPurchase }) => {
						ctx.patchState({
							loadingUpcomingRequisitionsSale: false,
							loadingUpcomingRequisitionsPurchase: false,
							upcomingRequisitionsSale,
							upcomingRequisitionsPurchase,
						});
					},
					(err) => {
						LogRocket.error('Unable to load upcoming sales requisitions', err);
						ctx.patchState({ loadingUpcomingRequisitionsSale: false });
					},
				);

			const businessId = this.store.selectSnapshot(
				UserProfileState.getCurrentBusinessId,
			);

			const functionCalls = [
				this.undertakingsFunctions.getOutstandingUndertakings,
				this.requisitionsFunctions.getOutstandingRequisitions,
			].map((fn) =>
				fn
					.bind(
						this,
						dashboardFilter.property,
						dashboardFilter.value,
						businessId,
					)()
					.pipe(first())
					.toPromise(),
			);

			Promise.all(functionCalls)
				.then(([outstandingUndertakings, outstandingRequisitions]) => {
					ctx.patchState({
						outstandingUndertakings,
						outstandingRequisitions,
					});
				})
				.catch((error) => Logger.exception(error))
				.finally(() => {
					ctx.patchState({
						loadingOutstandingUndertakings: false,
						loadingOutstandingRequisitions: false,
					});
				});
		}
	}

	private unsubscribeAll(): void {
		[
			this.appointmentsQuerySubscription,
			this.closingsQuerySubscription,
			this.occupanciesQuerySubscription,
			this.requisitionsQuerySubscription,
			this.upcomingRequisitionsSubscription,
		].forEach((sub) => sub?.unsubscribe());
	}

	private filterByOfficeIdOrNull(matter: Matter, officeId: string): boolean {
		return !matter?.officeId || matter.officeId === officeId;
	}
}
